// Globals
import 'rc-slider/assets/index.css';
import React, { useCallback, useEffect, useState } from 'react';

// UI
import AvatarEditor from 'react-avatar-editor';
import {
  CloseCircleOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Button, message, Slider, Tooltip, Upload } from 'antd';

// Locals
import styles from './ImageEditor.module.css';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  setImageChanged,
  setImageEditorImage,
  setImageMimeTypeLoaded,
  setNewImgLoadedBlob,
  updateCardProps,
} from '../../actions';
import { FeedCard, IState } from '../../graphql/types';
import {
  ImageEditorPosition,
  ImageEditorProps,
  ImageEditorState,
  ImageValidatorResponseType,
} from '../../types';
import useHoldPress from '../../hooks/useHoldPress';
import { checkImageAttributes } from '../utils';

interface IProps {
  props: ImageEditorProps;
}

const ImageEditor: React.FC<IProps> = ({ props }) => {
  const dispatch = useDispatch();

  const imageEditorState = useSelector<IState, ImageEditorState | undefined>(
    state => state.imageEditorState
  );

  const newImgLoadedBlob = useSelector<IState, string | undefined>(
    state => state.newImgLoadedBlob
  );

  const [scale, setScale] = useState<number>(imageEditorState?.scale ?? 1);

  const [position, setPosition] = useState<ImageEditorPosition | undefined>(
    imageEditorState?.position ?? undefined
  );

  const handleSetScale = e => {
    setScale(e);

    if (!imageHasChanged) {
      batch(() => {
        dispatch(setImageChanged(true));
        dispatch(
          updateCardProps({
            url: '',
          })
        );
      });
    }
  };

  const handleSetPosition = e => {
    setPosition(e);

    if (!imageHasChanged) {
      batch(() => {
        dispatch(setImageChanged(true));
        dispatch(
          updateCardProps({
            url: '',
          })
        );
      });
    }
  };

  const cardProps = useSelector<IState, Partial<FeedCard> | undefined>(
    state => state.cardProps || undefined
  );

  const handleLoadImage = useCallback(
    async file => {
      const imageValidation: ImageValidatorResponseType =
        await checkImageAttributes(
          file,
          undefined,
          props.minWidth,
          props.minHeight
        );

      if (imageValidation.isValid) {
        batch(() => {
          dispatch(setImageChanged(true));
          dispatch(setNewImgLoadedBlob(URL.createObjectURL(file)));
          dispatch(setImageMimeTypeLoaded(file.type.toLowerCase()));
          dispatch(
            setImageEditorImage({
              objectUrl: URL.createObjectURL(file),
              name: file.name,
              type: file.type,
              extension:
                file.name.slice(file.name.lastIndexOf('.')) || 'undefined',
            })
          );
        });

        // This prop handles all the tasks that are specific to the caller
        // (e.g. Profile Card, Graphic Card, Avatar Image, etc.)
        props.handleLoadImage(file);
      } else {
        message.error(imageValidation.error);
      }
    },
    [dispatch, props]
  );

  const imageHasChanged = useSelector<IState, boolean | undefined>(
    state => state.imageChanged
  );

  const handleZooming = useCallback(
    (type: string) => {
      if (type === 'plus') {
        if (scale >= 1 && scale < 10.9) setScale(scale + 0.1);
        else if (scale >= 10.9) setScale(11);
      } else if (type === 'minus') {
        if (scale > 1.1 && scale <= 11) setScale(scale - 0.1);
        else if (scale <= 1.1) setScale(1);
      }

      if (!imageHasChanged) {
        dispatch(setImageChanged(true));
      }
    },
    [scale, dispatch, imageHasChanged]
  );

  const zoomIn = useHoldPress(() => handleZooming('plus'), 100);
  const zoomOut = useHoldPress(() => handleZooming('minus'), 100);

  useEffect(() => {
    if (imageEditorState) {
      setPosition(imageEditorState.position);
      setScale(imageEditorState.scale || 1);
    }

    if (!props.image && !imageEditorState) {
      setScale(1);
      setPosition(undefined);
    }
  }, [imageEditorState, dispatch, props, cardProps]);

  return (
    <div
      className={`${styles.uploadEditorWrapper} ${
        props.validationFailed ? styles.missingImage : ''
      }`}
    >
      {props.loading && (
        <div
          className={`${styles.loadingPreviewWrapper} ${props.boxSizeClass} ${
            props.boxSizeClass
              ? props.boxSizeClass
              : props.loadingBoxClass ?? styles.loadingBox
          }`}
        >
          <LoadingOutlined spin className={styles.loadingPreviewIcon} />
        </div>
      )}

      {!props.loading && (
        <>
          {props.image && !props.uploading && !props.loading ? (
            <>
              <div className={styles.editorWrapper}>
                {newImgLoadedBlob && !props.disableEditor ? (
                  <>
                    <div className={styles.editorPositionText}>
                      Drag to reposition image
                    </div>
                    <AvatarEditor
                      className={`${
                        props.avatarEditorClass
                          ? props.avatarEditorClass
                          : styles.avatarEditor
                      } ${props.disabled ? styles.disabledWrapper : ''}`}
                      ref={props.setEditorRef}
                      image={props.image}
                      width={props.imgWidth}
                      height={props.imgHeight}
                      border={[props.imgBorderWidth, props.imgBorderHeight]}
                      borderRadius={props.imgBorderRadius}
                      color={[250, 250, 250, 0.9]}
                      scale={scale}
                      position={position}
                      onPositionChange={handleSetPosition}
                      crossOrigin={'anonymous'}
                    />
                    <div
                      className={`${styles.scaleSliderWrapper} scaleSliderWrapper`}
                    >
                      <MinusOutlined
                        className={`${styles.scaleSliderIcons} ${
                          props.disabled ? styles.disabledWrapper : ''
                        }`}
                        {...zoomOut}
                      />
                      <Slider
                        className={styles.scaleSlider}
                        style={{ width: `${props.sliderWidth}pt` }}
                        defaultValue={scale}
                        value={scale}
                        min={1}
                        max={11}
                        step={0.01}
                        onChange={handleSetScale}
                        tooltipVisible={false}
                        disabled={props.disabled}
                      />
                      <PlusOutlined
                        className={`${styles.scaleSliderIcons} ${
                          props.disabled ? styles.disabledWrapper : ''
                        }`}
                        {...zoomIn}
                      />
                    </div>
                  </>
                ) : (
                  <div
                    className={`${styles.staticImageWrapper} ${props.boxSizeClass}`}
                  >
                    <img
                      className={props.staticImageClass}
                      src={props.image}
                      alt="custom img"
                    />
                  </div>
                )}

                {!props.buttons ? (
                  <Tooltip
                    title={props.fileName.length > 30 ? props.fileName : ''}
                  >
                    <div
                      className={`${props.removeTextClass} ${
                        styles.removeText
                      } ${props.disabled ? styles.disabledWrapper : ''}`}
                    >
                      {props.fileName.length > 30
                        ? props.fileName.slice(0, 30) + '...'
                        : props.fileName}
                      <Button
                        type={'link'}
                        danger={true}
                        onClick={props.handleRemove}
                        icon={<CloseCircleOutlined />}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  props.buttons
                )}
              </div>
            </>
          ) : (
            <Upload.Dragger
              className={`${props.boxSizeClass} ${
                props.validationFailed ? styles.missingImage : ''
              }`}
              name="file"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleLoadImage}
              listType={'picture-card'}
              disabled={props.disabled}
            >
              <div style={{ padding: '48px 48px' }}>
                {!props.hideUploadIcon && (
                  <UploadOutlined style={{ fontSize: '48px' }} />
                )}
                {!props.text ? (
                  <>
                    <div>Choose an image</div>
                    <div>or drag it here.</div>
                  </>
                ) : (
                  props.text
                )}
              </div>
            </Upload.Dragger>
          )}
        </>
      )}
    </div>
  );
};

export default ImageEditor;
